// ==============================================
// Admin API Keys Page - Manage system-wide API keys
// ==============================================
// Keys are served to all Scout devices via /api/config/keys.
// Changes here take effect on next device fetch (typically within 24h,
// or immediately on app restart).

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { EyeIcon, EyeSlashIcon, CheckIcon } from '@heroicons/react/24/outline';

interface KeyConfig {
  id: string;
  label: string;
  provider: string;
  description: string;
  placeholder: string;
}

const KEY_DEFINITIONS: KeyConfig[] = [
  {
    id: 'google',
    label: 'Google Maps',
    provider: 'Google Cloud Platform',
    description: 'Satellite and hybrid basemap tiles (Maps Tiles API)',
    placeholder: 'AIzaSy...',
  },
  {
    id: 'esri',
    label: 'Esri / ArcGIS',
    provider: 'ArcGIS Location Platform',
    description: 'Satellite imagery, ocean basemap, and NOAA charts',
    placeholder: 'AAPTxy8...',
  },
  {
    id: 'azure',
    label: 'Azure Maps',
    provider: 'Microsoft Azure',
    description: 'Satellite imagery basemap (alternative provider)',
    placeholder: 'BtEv2NJw...',
  },
  {
    id: 'mapbox',
    label: 'Mapbox',
    provider: 'Mapbox',
    description: 'Satellite imagery basemap (alternative provider)',
    placeholder: 'pk.eyJ1...',
  },
  {
    id: 'planet',
    label: 'Planet Labs',
    provider: 'Planet',
    description: 'High-resolution satellite mosaics',
    placeholder: 'PLAKe...',
  },
];

function maskKey(key: string): string {
  if (key.length <= 12) return key;
  return key.slice(0, 8) + '...' + key.slice(-4);
}

export function AdminApiKeysPage() {
  const queryClient = useQueryClient();
  const [keys, setKeys] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [savedKeys, setSavedKeys] = useState<Record<string, string>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'api-keys'],
    queryFn: () => api.adminGetApiKeys(),
  });

  const updateMutation = useMutation({
    mutationFn: (newKeys: Record<string, string>) => api.adminUpdateApiKeys(newKeys),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'api-keys'] });
      setHasChanges(false);
      setSavedKeys(result);
    },
  });

  useEffect(() => {
    if (data) {
      setKeys(data);
      setSavedKeys(data);
    }
  }, [data]);

  const handleStartEdit = (id: string) => {
    setEditing(id);
    setEditValue(keys[id] || '');
  };

  const handleConfirmEdit = (id: string) => {
    const trimmed = editValue.trim();
    const newKeys = { ...keys };
    if (trimmed) {
      newKeys[id] = trimmed;
    } else {
      delete newKeys[id];
    }
    setKeys(newKeys);
    setEditing(null);
    setHasChanges(JSON.stringify(newKeys) !== JSON.stringify(savedKeys));
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setEditValue('');
  };

  const toggleReveal = (id: string) => {
    setRevealed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSaveAll = () => {
    updateMutation.mutate(keys);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
        Failed to load API keys. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">API Keys</h1>
          <p className="text-sm text-slate-400 mt-1">
            Manage API keys for all Scout devices. Changes are picked up automatically within 24 hours.
          </p>
        </div>
        {hasChanges && (
          <button
            onClick={handleSaveAll}
            disabled={updateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {updateMutation.isPending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckIcon className="w-4 h-4" />
            )}
            Save Changes
          </button>
        )}
      </div>

      {updateMutation.isSuccess && !hasChanges && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-green-400 text-sm">
          Keys saved. Devices will pick up the new keys on their next sync.
        </div>
      )}

      {updateMutation.isError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
          Failed to save keys. Please try again.
        </div>
      )}

      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700/50">
              <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-3">Provider</th>
              <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-3">Key</th>
              <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-3">Status</th>
              <th className="text-right text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/30">
            {KEY_DEFINITIONS.map(def => {
              const value = keys[def.id] || '';
              const isEditing = editing === def.id;
              const isRevealed = revealed.has(def.id);

              return (
                <tr key={def.id} className="hover:bg-slate-700/20 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-white">{def.label}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{def.description}</div>
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          placeholder={def.placeholder}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-md px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-cyan-500"
                          autoFocus
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleConfirmEdit(def.id);
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                        />
                        <button
                          onClick={() => handleConfirmEdit(def.id)}
                          className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-sm rounded-md"
                        >
                          OK
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-md"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <code className="text-sm text-slate-300 font-mono">
                        {value ? (isRevealed ? value : maskKey(value)) : <span className="text-slate-500 italic">not set</span>}
                      </code>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {value ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                        active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-500/10 text-slate-400 border border-slate-500/20">
                        not set
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {value && !isEditing && (
                        <button
                          onClick={() => toggleReveal(def.id)}
                          className="p-1.5 text-slate-400 hover:text-white transition-colors"
                          title={isRevealed ? 'Hide key' : 'Reveal key'}
                        >
                          {isRevealed ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                        </button>
                      )}
                      {!isEditing && (
                        <button
                          onClick={() => handleStartEdit(def.id)}
                          className="px-3 py-1 text-sm text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 rounded-md transition-colors"
                        >
                          {value ? 'Edit' : 'Set'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-slate-800/30 border border-slate-700/30 rounded-lg p-4 text-xs text-slate-400">
        <p className="font-medium text-slate-300 mb-1">How this works</p>
        <p>
          Keys saved here are served to all Scout devices via the cloud API. Devices fetch keys on startup
          and cache them locally for offline use. A key change here takes effect on each device's next
          internet connection — no OTA update required.
        </p>
      </div>
    </div>
  );
}
